import { Image } from '@chakra-ui/react'
import { Handle, Position, useReactFlow } from '@xyflow/react'
import { ExternalLink } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import genericLogo from '@/assets/generic.png'
import { trackExternalLink } from '~/lib/external-links'

interface DynamicNodeData {
  label: string
  name?: string
  logoUrl?: string
  backgroundColor?: string
  externalLink?: string
}

interface DynamicNodeProps {
  id: string
  data: DynamicNodeData
}

function DynamicNode({ id, data }: DynamicNodeProps) {
  const { setNodes } = useReactFlow()
  const [componentName, setComponentName] = useState(data.name || data.label || 'Node')

  // Update node data when name changes (debounced on blur)
  const updateNodeName = useCallback(
    (newName: string) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                name: newName,
              },
            }
          }
          return node
        }),
      )
    },
    [id, setNodes],
  )
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [componentName])

  // Use logoUrl from data if available, otherwise fall back to generic
  const logoSrc = data.logoUrl || genericLogo

  // Use backgroundColor from data, default to white
  const bgColor = data.backgroundColor || '#FFFFFF'

  // Check if this is a HiveMQ node (yellow background)
  const isHiveMQNode = bgColor === '#FFC000' || bgColor === '#ffc000'

  // Dynamic styles based on background color
  const nodeStyle = {
    backgroundColor: bgColor,
  }

  // Text color should be white for dark backgrounds (like HiveMQ yellow)
  const textColorClass = isHiveMQNode ? 'pg-hivemq-text' : ''

  const handleExternalLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (data.externalLink) {
      // Track the external link click
      const linkName = data.name || data.label || 'Playground Node'
      trackExternalLink(data.externalLink, linkName)
      window.open(data.externalLink, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div className={`pg-dynamic-node ${textColorClass}`} style={nodeStyle}>
      <Handle type="target" position={Position.Left} id="ta" className="pg-target-connection" />
      <Handle type="target" position={Position.Right} id="tb" className="pg-target-connection" />
      <Handle type="target" position={Position.Bottom} id="tc" className="pg-target-connection" />
      <Handle type="target" position={Position.Top} id="td" className="pg-target-connection" />

      <Handle type="source" position={Position.Right} id="sa" className="pg-source-connection" />
      <Handle type="source" position={Position.Left} id="sb" className="pg-source-connection" />
      <Handle type="source" position={Position.Bottom} id="sc" className="pg-source-connection" />
      <Handle type="source" position={Position.Top} id="sd" className="pg-source-connection" />
      {data.externalLink && (
        <button
          type="button"
          onClick={handleExternalLinkClick}
          className="pg-external-link-btn nodrag"
          title="Open external link"
          aria-label="Open external link"
        >
          <ExternalLink size={14} />
        </button>
      )}
      <Image className="pg-dynamic-logo" src={logoSrc} />
      <div className="pg-dynamic-text pg-text-dynamic-input">
        <textarea
          ref={textareaRef}
          id="text"
          name="componentName"
          onChange={(e) => {
            setComponentName(e.target.value)
            updateNodeName(e.target.value)
          }}
          value={componentName}
          className="nodrag pg-node-textarea"
          rows={1}
        />
      </div>
    </div>
  )
}

export default DynamicNode
